import Button from '@rio-cloud/rio-uikit/Button';
import { useEffect, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';

import { TTabConfig } from '@/components/molecule/UiKitTabs/types';
import UiKitTabs from '@/components/molecule/UiKitTabs/UiKitTabs';
import { requestComposerFocus } from '@/store/composerFocus/composerFocusSlice';
import { disableOnboardingMode } from '@/store/onboarding/onboardingSlice';
import { closeSidebar, openSidebar } from '@/store/sidebar/sidebarSlice';
import { removeOnboardingThread, resetActiveThread } from '@/store/thread/threadSlice';

import { useRemainingHeight } from '../../../hooks/useRemainingHeight';
import AllThreads from './AllThreads/AllThreads';
import FavoritesThreads from './FavoriteThreads/FavoritesThreads';
import { ThreadsHistoryWrapper } from './Style';
import { SCROLLABLE_SECTION } from './ThreadsList/constants';
import { TThreadsHistoryProps, TThreaTab } from './types';

const ThreadsHistory = ({ isLoading: isSendMessageLoading }: TThreadsHistoryProps) => {
  const intl = useIntl();
  const location = useLocation();
  const dispatch = useDispatch();
  const isSmallScreen = useMediaQuery({ query: '(max-width: 47.5rem)' });
  const containerRef = useRef<HTMLDivElement>(null);

  useRemainingHeight(containerRef, `[id^='${SCROLLABLE_SECTION}']`);

  const defaultTabFromState = (location.state as { defaultTab?: TThreaTab })?.defaultTab;
  const initialTab = defaultTabFromState ?? 'allChats';

  useEffect(() => {
    if (defaultTabFromState) {
      dispatch(openSidebar());

      // INFO: Clears the route redirect state so reloads don't re-trigger this logic
      window.history.replaceState({}, '');
    }
  }, [defaultTabFromState]);

  // INFO: Config object for UiKitTabs
  const tabConfig: TTabConfig<TThreaTab> = useMemo(
    () => ({
      allChats: {
        label: intl.formatMessage({ id: 'chatPage.sidebar.history.label' }),
        content: <AllThreads />,
        disabled: isSendMessageLoading,
      },
      favoriteChats: {
        label: intl.formatMessage({ id: 'chatPage.sidebar.favorites.label' }),
        content: <FavoritesThreads />,
        disabled: isSendMessageLoading,
      },
    }),
    [isSendMessageLoading],
  );

  const handleNewChatClick = () => {
    dispatch(resetActiveThread());
    dispatch(disableOnboardingMode());
    dispatch(removeOnboardingThread());

    // INFO: Starting a new chat has no purpose other than typing a question, so the composer is
    // asked for the caret. It is a request rather than a direct focus call because the composer
    // lives in a sibling subtree, and because it must be honoured only once the composer accepts
    // input and no dialog is open.
    dispatch(requestComposerFocus());

    if (isSmallScreen) {
      dispatch(closeSidebar());
    }
  };

  return (
    <ThreadsHistoryWrapper ref={containerRef} className="display-flex flex-column justify-content-start height-100pct">
      <div className="display-flex justify-content-start padding-x-10 margin-x-10 margin-y-40">
        <Button
          className="width-100pct"
          bsStyle="default"
          iconName="rioglyph rioglyph-plus-light"
          disabled={isSendMessageLoading}
          onClick={handleNewChatClick}
          data-testid="add-new-chat"
        >
          <FormattedMessage id="chatPage.chatThreadList.threadDeletion.addNewChat.label" />
        </Button>
      </div>
      <UiKitTabs<TThreaTab> tabConfig={tabConfig} defaultTab={initialTab} />
    </ThreadsHistoryWrapper>
  );
};

export default ThreadsHistory;
